import {register} from '@shopify/theme-sections';

import ModalDialog from '../components/modal-dialog';
import initPageScrollProgress from '../components/page-progress';
import initLoginModal from '../components/login-modal';
import {debounce} from '../utils/debounce';

const selectors = {
  navItem: '[data-nav-item]',
  navOverlay: '[data-nav-fader]',
  navDrawerTrigger: '[data-modal-nav-trigger]',
  navDrawer: '[data-modal-nav]',
  navDropdown: '[data-nav-dropdown]',
  activeDropdownLinks: '.nav__item--expanded',
  megamenu: '[data-megamenu]',
  navLevel2: '[data-nav-level-2]',
  navLevel3: '[data-nav-level-3]',
  navLinkLevel1: '[data-nav-link-level-1]',
  navLinkLevel2: '[data-nav-link-level-2]',
  containerNavLevel2: '[data-container-nav-level-2]',
  containerNavLevel3: '[data-container-nav-level-3]',
  containerNavLevel3Desktop: '[data-container-nav-level-3-desktop]',
  navCloseLevel2: '[data-nav-close-level-2]',
  navLevel3Close: '[data-nav-level-3-close]',
  megamenuLevel2Header: '[data-megamenu-level-2-header]',
  dropdownFader: '[data-dropdown-fader]',
  megamenuImages: '[data-megamenu-images]',
};

const classes = {
  headerSticky: 'header-section--sticky',
  headerPinned: 'header-section--pinned',
  isExpanded: 'nav__item--expanded',
  level2IsExpanded: 'nav__item--level-2-expanded',
  level2LinkExpanded: 'nav-level-2__link--expanded',
  dropdownIsActive: 'dropdown-is-active',
  navLevel3Active: 'nav-level-3--active',
  navLevel2LinkActive: 'nav-level-2__link--active',
  navHidden: 'nav-hidden',
};

const sectionType = 'header';
const registeredSections = window.Shopify?.theme?.sections?.registered || {};
const isUnregistered = typeof registeredSections[sectionType] === 'undefined';

export default isUnregistered &&
  register(sectionType, {
    onLoad() {
      this.header = this.container;
      this.headerSection = this.header.parentElement;
      this.navDrawer = this.container.parentElement.querySelector(selectors.navDrawer);
      this.dropdownFader = this.headerSection.querySelector(selectors.dropdownFader);

      const sectionSettings = this.container.dataset.sectionSettings || '{}';
      this.settings = JSON.parse(sectionSettings);

      this.bodyClassRemoveTimeout = undefined;

      this.desktopQuery = window.matchMedia(`(min-width: ${window.theme.breakpoints.lg}px)`);

      this.setHeaderHeightVar();

      this.headerPositionObserver();

      this.setNavDrawerAttr();

      this.calculateNavDropdownMaxHeight();

      // Handle nav item (level 1 <li>) mouseover & mouseleave events (desktop only)
      const navItems = this.header.querySelectorAll(selectors.navItem);
      navItems?.forEach((navItem) => {
        // Skip items without dropdowns
        const navLink = navItem.querySelector(selectors.navLinkLevel1);
        const navDropdown = navItem.querySelector(selectors.navDropdown);
        if (!navLink || !navDropdown) return;

        navItem.addEventListener('mouseenter', () => {
          if (!this.desktopQuery.matches) return;

          clearTimeout(this.bodyClassRemoveTimeout);
          this.expandNavItem(navItem, navLink, navDropdown);
        });

        navItem.addEventListener('mouseleave', () => {
          if (!this.desktopQuery.matches) return;

          this.collapseNavItem(navItem, navLink, navDropdown);
        });
      });

      // Handle nav level 1 links click
      this.navLinksLevel1 = this.header.querySelectorAll(selectors.navLinkLevel1);
      this.navLinksLevel1.forEach((link) => link.addEventListener('click', this.navLinkLevel1ClickHandler.bind(this)));

      // Handle megamenu mouseover & mouseleave events (desktop only)
      const megamenus = this.container.querySelectorAll(selectors.megamenu);
      megamenus.forEach((menu) => {
        const navItem = menu.closest(selectors.navItem);
        if (!navItem) return;

        menu.addEventListener('mouseover', () => {
          // console.log('Megamenu Mouseover:', menu); // does not log
          if (!this.desktopQuery.matches) return;
          navItem.classList.add(classes.level2IsExpanded);
        });

        menu.addEventListener('mouseleave', () => {
          // Restore mouseleave behavior for desktop
          if (!this.desktopQuery.matches) return;
          navItem.classList.remove(classes.level2IsExpanded);
          this.resetMegamenu(menu);
        });
      });

      // Handle nav link level 2 events
      const navLinksLevel2 = this.container.querySelectorAll(selectors.navLinkLevel2);
      navLinksLevel2.forEach((navLinkLevel2) => {
        // Desktop event - changed from click back to mouseenter
        navLinkLevel2.addEventListener('mouseenter', this.navLinkLevel2MouseHandler.bind(this));

        // Mouseleave event listener (desktop only) - reset items that don't have a level 3 nav
        navLinkLevel2.addEventListener('mouseleave', () => {
          if (!this.desktopQuery.matches) return;
          const indexNavLinkLevel2 = navLinkLevel2.getAttribute('data-nav-link-level-2');
          if (indexNavLinkLevel2) {
            return;
          }
          navLinkLevel2.classList.remove(classes.navLevel2LinkActive);
        });

        // Click event listener (mobile only)
        navLinkLevel2.addEventListener('click', this.navLinkLevel2ClickHandler.bind(this));
      });

      // Handle nav level 2 close button click (mobile only)
      const navCloseLevel2 = this.header?.querySelectorAll(selectors.navCloseLevel2);
      navCloseLevel2.forEach((el) => el.addEventListener('click', this.navCloseLevel2ClickHandler.bind(this)));

      // Handle nav level 3 close button click (mobile only)
      const navLevel3Close = this.header?.querySelectorAll(selectors.navLevel3Close);
      navLevel3Close.forEach((el) => el.addEventListener('click', this.navCloseLevel3ClickHandler.bind(this)));

      // Handle nav fader click
      const navOverlays = this.headerSection.querySelectorAll(selectors.navOverlay);
      navOverlays?.forEach((navOverlay) => {
        navOverlay.addEventListener('click', this.activeMenuCloseHandler.bind(this));
      });

      // Initialise nav drawer on mobile
      const navDrawerTrigger = this.container.querySelectorAll(selectors.navDrawerTrigger);
      if (navDrawerTrigger.length > 0 && this.navDrawer) {
        // eslint-disable-next-line no-unused-vars
        const navDrawerInstance = new ModalDialog(this.navDrawer, {
          triggerEl: navDrawerTrigger,
          modalId: 'nav',
        });
      }

      const desktopQueryCallback = () => {
        this.handleResize();
      };

      // Check for `addEventListener` is required to support iOS and Safari <14.
      if (typeof this.desktopQuery?.addEventListener === 'function') {
        this.desktopQuery.addEventListener('change', desktopQueryCallback.bind(this));
      } else {
        this.desktopQuery.addListener(desktopQueryCallback.bind(this));
      }

      initPageScrollProgress(this.headerSection);
      initLoginModal();

      const debouncedWindowResize = debounce(() => {
        this.calculateNavDropdownMaxHeight();

        const activeDropdown = this.container.querySelector(selectors.activeDropdownLinks);
        const navDropdown = activeDropdown?.querySelector(selectors.navDropdown);
        this.setDropdownFaderHeight(navDropdown);
      }, 100);

      window.addEventListener('resize', debouncedWindowResize);
    },

    setHeaderHeightVar() {
      window.requestAnimationFrame(() => {
        const headerHeight = this.container.offsetHeight;
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
      });
    },

    setNavDrawerAttr() {
      const navDrawer = this.container.querySelector('[data-modal-nav]');
      if (!this.navDrawer) return;

      if (this.desktopQuery.matches) {
        navDrawer.removeAttribute('aria-hidden');
        return;
      }

      navDrawer.setAttribute('aria-hidden', true);
    },

    handleResize() {
      this.activeMenuCloseHandler();
      this.setHeaderHeightVar();
      this.setNavDrawerAttr();
    },

    activeMenuCloseHandler() {
      const activeDropdownLinks = this.container.querySelectorAll(selectors.activeDropdownLinks);

      activeDropdownLinks.forEach((el) => {
        el.classList.remove(classes.isExpanded, classes.level2IsExpanded);
        const link = el.querySelector(selectors.navLinkLevel1);
        link?.setAttribute('aria-expanded', false);
        this.closeNavLevel3(el);
      });

      this.removeBodyClassWithDelay();
    },

    setDropdownFaderHeight(dropdown) {
      if (!dropdown || !this.desktopQuery.matches) return;
      const navMaxHeightProperty = getComputedStyle(dropdown).getPropertyValue('--nav-max-height');
      const navMaxHeight = Number(navMaxHeightProperty.replace('px', ''));
      this.dropdownFader.style.height = `${navMaxHeight + 48}px`;
    },

    removeBodyClassWithDelay() {
      let delay = 0;
      if (this.desktopQuery.matches) {
        delay = 400;
      }

      this.bodyClassRemoveTimeout = setTimeout(() => {
        document.body.classList.remove(classes.dropdownIsActive);
      }, delay);
    },

    expandNavItem(navItem, navLink, navDropdown) {
      // Check if header is positioned at the bottom and scroll page so dropdown is visible
      const navDropdownRect = navDropdown.getBoundingClientRect();

      if (
        this.desktopQuery.matches &&
        document.body.classList.contains('has-header-bottom') &&
        window.innerHeight - navDropdownRect.bottom < 8
      ) {
        window.scrollTo({
          top: navDropdownRect.bottom - window.innerHeight + window.scrollY + 8,
        });
      }

      this.setDropdownFaderHeight(navDropdown);

      navItem.classList.add(classes.isExpanded);
      navLink.setAttribute('aria-expanded', true);
      document.body.classList.add(classes.dropdownIsActive);

      this.handleDropdownHiddenState(navDropdown, 'visible');

      // Desktop only: Auto-open first level 3 child when hovering on navLinkLevel1
      if (this.desktopQuery.matches) {
        const megamenu = navDropdown.querySelector(selectors.megamenu);
        if (megamenu) {
          const containerNavLevel3 = megamenu.querySelector(selectors.containerNavLevel3Desktop);
          if (containerNavLevel3) {
            // Close any previously open L3
            this.closeNavLevel3(megamenu);

            // Find the first level 2 link that has level 3 children
            const firstNavLinkLevel2 = megamenu.querySelector(selectors.navLinkLevel2);
            if (firstNavLinkLevel2) {
              const indexNavLinkLevel2 = firstNavLinkLevel2.getAttribute('data-nav-link-level-2');
              if (indexNavLinkLevel2) {
                // Activate the first level 2 link
                firstNavLinkLevel2.classList.add(classes.navLevel2LinkActive);

                // Open the corresponding level 3 content
                const navLevel3Selected = containerNavLevel3.querySelector(
                  `[data-nav-level-3="${indexNavLinkLevel2}"]`
                );
                if (navLevel3Selected) {
                  navLevel3Selected.classList.add(classes.navLevel3Active);
                }
              }
            }
          }
        }
      }
    },

    collapseNavItem(navItem, navLink, navDropdown) {
      navLink.setAttribute('aria-expanded', false);
      navItem.classList.remove(classes.isExpanded);

      this.removeBodyClassWithDelay();

      this.handleDropdownHiddenState(navDropdown, 'hidden');
    },

    navLinkLevel1ClickHandler(event) {
      event.preventDefault();

      clearTimeout(this.bodyClassRemoveTimeout);

      // Menu item toggle
      const navItem = event.currentTarget.parentElement;
      const navLink = event.currentTarget;
      const navDropdown = navItem.querySelector(selectors.navDropdown);

      if (navItem.classList.contains(classes.isExpanded)) {
        this.collapseNavItem(navItem, navLink, navDropdown);
      } else {
        this.navLinksLevel1.forEach((el) => {
          el.parentElement.classList.remove(classes.isExpanded);
          el.setAttribute('aria-expanded', false);
        });

        this.expandNavItem(navItem, navLink, navDropdown);
      }

      return true;
    },

    navLinkLevel2MouseHandler(event) {
      // Only for desktop
      if (!this.desktopQuery.matches) {
        return;
      }

      // Prevent navigation for a.href links and stop bubbling
      event.preventDefault();
      event.stopPropagation();

      const navLinkLevel2 = event.currentTarget;
      const megamenu = navLinkLevel2.closest(selectors.megamenu);
      const indexNavLinkLevel2 = navLinkLevel2.getAttribute('data-nav-link-level-2');
      const containerNavLevel3 = megamenu?.querySelector(selectors.containerNavLevel3Desktop);

      // Close any previously open L3
      this.closeNavLevel3(megamenu);

      navLinkLevel2.classList.add(classes.navLevel2LinkActive);

      if (!containerNavLevel3 || !indexNavLinkLevel2) {
        return;
      }

      const navLevel3Selected = containerNavLevel3.querySelector(`[data-nav-level-3="${indexNavLinkLevel2}"]`);
      if (!navLevel3Selected) {
        return;
      }

      navLevel3Selected.classList.add(classes.navLevel3Active);
    },

    closeNavLevel3(container) {
      if (!container) return;

      const activeLinkLevel2 = container.querySelector(`.${classes.navLevel2LinkActive}`);
      activeLinkLevel2?.classList.remove(classes.navLevel2LinkActive);

      const activeNavLevel3 = container.querySelector(`.${classes.navLevel3Active}`);
      activeNavLevel3?.classList.remove(classes.navLevel3Active);
      this.handleDropdownHiddenState(activeNavLevel3, 'hidden');
    },

    resetMegamenu(container) {
      if (!this.desktopQuery.matches) return;

      this.closeNavLevel3(container);

      const containerNavLevel3 = container?.querySelector(selectors.containerNavLevel3);
      if (!containerNavLevel3) return;
      containerNavLevel3.style.transform = 'translateY(0px)';
    },

    navLinkLevel2ClickHandler(event) {
      // Only for mobile
      if (this.desktopQuery.matches) return;

      // The clicked L2 link and its parent L1 container
      const navLinkLevel2 = event.currentTarget;
      const navItem = navLinkLevel2.closest(selectors.navItem);
      if (!navItem) return;

      const indexNavLinkLevel2 = navLinkLevel2.getAttribute('data-nav-link-level-2');

      // Regular link without L3 children - allow normal navigation
      if (!indexNavLinkLevel2) {
        return;
      }

      // Has L3 children - prevent navigation and toggle accordion
      event.preventDefault();

      const navLevel3ToToggle = navItem.querySelector(`[data-nav-level-3="${indexNavLinkLevel2}"]`);
      if (!navLevel3ToToggle) return;

      const isCurrentlyOpen = navLevel3ToToggle.classList.contains(classes.navLevel3Active);

      if (isCurrentlyOpen) {
        // Close open L3 panel
        navLevel3ToToggle.classList.remove(classes.navLevel3Active);
        this.handleDropdownHiddenState(navLevel3ToToggle, 'hidden');
        navItem.classList.remove(classes.level2IsExpanded);
        navLinkLevel2.classList.remove(classes.level2LinkExpanded);
      } else {
        // Close any other open L3 panel first
        const currentlyActiveL3 = navItem.querySelector(`.${classes.navLevel3Active}`);
        if (currentlyActiveL3 && currentlyActiveL3 !== navLevel3ToToggle) {
          currentlyActiveL3.classList.remove(classes.navLevel3Active);
          this.handleDropdownHiddenState(currentlyActiveL3, 'hidden');

          const previouslyActiveLink = navItem.querySelector(`.${classes.level2LinkExpanded}`);
          if (previouslyActiveLink) {
            previouslyActiveLink.classList.remove(classes.level2LinkExpanded);
          }
        }

        // Open the selected L3 panel
        navLevel3ToToggle.classList.add(classes.navLevel3Active);
        this.handleDropdownHiddenState(navLevel3ToToggle, 'visible');
        navItem.classList.add(classes.level2IsExpanded);
        navLinkLevel2.classList.add(classes.level2LinkExpanded);

        // Update header text
        const headerLevel2 = navItem.querySelector(selectors.megamenuLevel2Header);
        if (headerLevel2) {
          headerLevel2.textContent = navLinkLevel2.textContent;
        }
      }
    },

    navCloseLevel2ClickHandler() {
      const navItemExpanded = this.navDrawer.querySelector(`.${classes.isExpanded}`);
      const navLinkExpanded = navItemExpanded?.querySelector('a');
      const navDropdown = navItemExpanded?.querySelector(selectors.navDropdown);

      navLinkExpanded.setAttribute('aria-expanded', false);
      navItemExpanded.classList.remove(classes.isExpanded);
      document.body.classList.remove(classes.dropdownIsActive);

      this.handleDropdownHiddenState(navDropdown, 'hidden');
    },

    navCloseLevel3ClickHandler(event) {
      const navItem = event.currentTarget.closest(selectors.navItem);

      navItem?.classList.remove(classes.level2IsExpanded);

      const activeNavLevel3 = navItem?.querySelector(`.${classes.navLevel3Active}`);
      activeNavLevel3?.classList.remove(classes.navLevel3Active);

      this.handleDropdownHiddenState(activeNavLevel3, 'hidden');
    },

    calculateNavDropdownMaxHeight() {
      const navDropdowns = this.header.querySelectorAll(selectors.navDropdown);
      navDropdowns.forEach((navDropdown) => {
        const navLevel2 = navDropdown.querySelector(selectors.containerNavLevel2);
        const megamenuImages = navDropdown.querySelector(selectors.megamenuImages);
        const navsLevel3 = Array.from(navDropdown.querySelectorAll(selectors.navLevel3));
        const navHeights = navsLevel3.map((nav) => nav.offsetHeight);

        const maxHeight = Math.max(...navHeights, navLevel2?.offsetHeight || 0, megamenuImages?.offsetHeight || 0);

        navDropdown.style.setProperty('--nav-max-height', `${maxHeight}px`);
      });
    },

    /**
     * Update nav dropdown state to fix scrolling issue from nav drawer (mobile)
     */

    handleDropdownHiddenState(nav, state = 'hidden') {
      if (!nav) return;

      if (state === 'visible') {
        nav.classList.remove(classes.navHidden);
        return;
      }

      nav?.addEventListener(
        'transitionend',
        () => {
          nav.classList.add(classes.navHidden);
        },
        {once: true}
      );
    },

    /**
     * Observes header position when header is transparent or
     * positioned at the bottom of the window (e.g. home and landing pages)
     * Updates class for header once user scrolls past first section
     */
    headerPositionObserver() {
      if (!this.settings.isSticky) return;

      if (!this.settings.isPositionedBottom && !this.settings.isTransparent) return;

      const firstSection = document.getElementById('MainContent')?.firstElementChild;

      const positionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (this.settings.isPositionedBottom) {
              this.headerSection.classList.toggle(classes.headerSticky, !entry.isIntersecting);
              return;
            }

            if (this.settings.isTransparent) {
              this.headerSection.classList.toggle(classes.headerPinned, !entry.isIntersecting);
            }
          });
        },
        {rootMargin: `${-1 * this.header.offsetHeight}px`}
      );

      if (this.desktopQuery.matches) {
        positionObserver.observe(firstSection);
      }

      // Connect / disconnect observer when media query changes
      this.desktopQuery.addEventListener('change', (event) => {
        if (event.matches) {
          positionObserver.observe(firstSection);
          return;
        }

        positionObserver.disconnect();
      });
    },
  });
